import { LogIn, Search, UtensilsCrossed, UserRoundCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { TableViewGuest } from '../components/TableViewGuest'

type Category={id:string;name:string;slug:string;sort_order:number}
type Product={id:string;category_id:string|null;name:string;description:string|null;price:number;promotional_price:number|null;image_url:string|null;available:boolean;stock_blocked:boolean;is_daily_special:boolean}

const money=new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'})

export function PublicMenu(){
  const [categories,setCategories]=useState<Category[]>([])
  const [products,setProducts]=useState<Product[]>([])
  const [search,setSearch]=useState('')
  const [loading,setLoading]=useState(true)
  const [error,setError]=useState('')
  const [showWaiterMessage,setShowWaiterMessage]=useState(false)

  useEffect(()=>{
    let active=true
    async function load(){
      setLoading(true);setError('')
      const {data,error:e}=await supabase.functions.invoke('public-menu',{body:{}})
      if(!active)return
      if(e||data?.error)setError(data?.error||e?.message||'Não foi possível carregar o cardápio.')
      else{
        setCategories((data?.categories||[]) as Category[])
        setProducts((data?.products||[]) as Product[])
      }
      setLoading(false)
    }
    void load()
    return()=>{active=false}
  },[])

  const filtered=useMemo(()=>{
    const q=search.trim().toLowerCase()
    if(!q)return products
    return products.filter(product =>
      product.name.toLowerCase().includes(q) ||
      (product.description||'').toLowerCase().includes(q)
    )
  },[products,search])

  const sections=useMemo(()=>{
    const byCategory=categories.map(category=>({
      ...category,
      products:filtered.filter(product=>product.category_id===category.id),
    })).filter(section=>section.products.length)
    const uncategorized=filtered.filter(product=>!product.category_id||!categories.some(category=>category.id===product.category_id))
    return uncategorized.length
      ? [...byCategory,{id:'outros',name:'Outros',slug:'outros',sort_order:9999,products:uncategorized}]
      : byCategory
  },[categories,filtered])

  function requestWaiter(){
    setShowWaiterMessage(true)
    window.setTimeout(()=>setShowWaiterMessage(false),4500)
  }

  return <main className="public-menu-page">
    <header className="public-menu-hero">
      <div className="public-menu-top">
        <div className="public-menu-brand">
          <span className="public-menu-brand-mark">G</span>
          <div><strong>GONÇALO</strong><small>MUSIC BAR</small></div>
        </div>
        <Link to="/acesso" className="public-menu-login"><LogIn size={17}/><span>Acesso da equipe</span><small>Admin · Caixa · Garçom</small></Link>
      </div>

      <div className="public-menu-hero-copy">
        <span>GONÇALO MUSIC BAR</span>
        <h1>Cardápio <em>Digital</em></h1>
        <p>Muito mais que um bar, um lugar para bons momentos.</p>
      </div>

      <div className="public-menu-search">
        <Search size={20}/>
        <input value={search} onChange={event=>setSearch(event.target.value)} placeholder="Busque cerveja, porção, drink, refrigerante..."/>
      </div>

      <div className="public-menu-order-note">
        <UserRoundCheck size={22}/>
        <div><strong>Gostou de alguma coisa?</strong><span>Faça seu pedido diretamente com um de nossos garçons.</span></div>
        <button type="button" onClick={requestWaiter}>Chamar garçom para pedir</button>
      </div>
    </header>

    <TableViewGuest/>

    {showWaiterMessage&&<div className="public-menu-toast">🙋 Chame um garçom da casa e informe os itens que deseja pedir.</div>}

    {!loading&&!error&&products.some(product=>product.is_daily_special)&&<section style={{padding:'24px 20px',background:'#fff8df',color:'#241a0c',borderBottom:'3px solid #e6b545'}}><div style={{maxWidth:1100,margin:'0 auto'}}><h2 style={{margin:'0 0 6px',fontSize:27,color:'#201509',fontWeight:900}}>🍽️ Pratos do dia</h2><p style={{margin:'0 0 14px',color:'#574426',fontWeight:500}}>Especialidades disponíveis hoje no Gonçalo Music Bar.</p><div className="public-menu-grid">{products.filter(product=>product.is_daily_special).map(product=><article className="public-menu-product" key={product.id}><div className="public-menu-product-image">{product.image_url?<img src={product.image_url} alt={product.name} loading="lazy"/>:<div className="public-menu-product-placeholder"><UtensilsCrossed size={28}/><span>GONÇALO</span></div>}</div><div className="public-menu-product-copy"><h3>{product.name}</h3>{product.description&&<p>{product.description}</p>}<div className="public-menu-price"><strong>{money.format(Number(product.promotional_price??product.price))}</strong></div></div></article>)}</div></div></section>}

    <nav className="public-menu-categories" aria-label="Categorias">
      {sections.map(section=><a key={section.id} href={'#menu-'+section.slug}>{section.name}</a>)}
    </nav>

    <section className="public-menu-content">
      {loading&&<div className="public-menu-state"><div className="public-menu-loader"/><strong>Carregando nosso cardápio…</strong></div>}
      {error&&<div className="public-menu-state error"><strong>Cardápio indisponível no momento</strong><p>{error}</p><button onClick={()=>window.location.reload()}>Tentar novamente</button></div>}
      {!loading&&!error&&sections.length===0&&<div className="public-menu-state"><UtensilsCrossed size={38}/><strong>Nenhum item disponível agora.</strong><p>Consulte nossa equipe.</p></div>}

      {!loading&&!error&&sections.map(section=><section className="public-menu-section" id={'menu-'+section.slug} key={section.id}>
        <div className="public-menu-section-title"><span>GONÇALO</span><h2>{section.name}</h2><small>{section.products.length} item(ns)</small></div>
        <div className="public-menu-grid">
          {section.products.map(product=>{
            const sale=product.promotional_price!=null&&Number(product.promotional_price)<Number(product.price)
            return <article className="public-menu-product" key={product.id}>
              <div className="public-menu-product-image">
                {product.image_url?<img src={product.image_url} alt={product.name} loading="lazy"/>:<div className="public-menu-product-placeholder"><UtensilsCrossed size={28}/><span>GONÇALO</span></div>}
                {sale&&<span className="public-menu-sale">OFERTA</span>}
              </div>
              <div className="public-menu-product-copy">
                <h3>{product.name}</h3>
                {product.description&&<p>{product.description}</p>}
                <div className="public-menu-price">
                  {sale&&<small>{money.format(Number(product.price))}</small>}
                  <strong>{money.format(Number(product.promotional_price??product.price))}</strong>
                </div>
              </div>
            </article>
          })}
        </div>
      </section>)}
    </section>

    <footer className="public-menu-footer">
      <div className="public-menu-brand">
        <span className="public-menu-brand-mark">G</span>
        <div><strong>GONÇALO</strong><small>MUSIC BAR</small></div>
      </div>
      <p>Consulte disponibilidade com nossa equipe. • Pedidos realizados diretamente com o garçom.</p>
      <Link to="/acesso"><LogIn size={16}/> Acesso da equipe</Link>
    </footer>
  </main>
}
